/**
 * Thin wrappers over aws-amplify/auth so component code stays terse.
 * Errors are normalized to message strings; component decides display.
 */

import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  confirmSignIn as amplifyConfirmSignIn,
  resendSignUpCode as amplifyResendCode,
  signOut as amplifySignOut,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
} from "aws-amplify/auth";

export async function signUp(email: string, password: string) {
  const result = await amplifySignUp({
    username: email,
    password,
    options: { userAttributes: { email } },
  });
  return result;
}

export async function confirmSignUp(email: string, code: string) {
  return amplifyConfirmSignUp({ username: email, confirmationCode: code });
}

export async function resendCode(email: string) {
  return amplifyResendCode({ username: email });
}

export async function signIn(email: string, password: string) {
  return amplifySignIn({ username: email, password });
}

/**
 * Continue a sign-in flow that returned a challenge (NEW_PASSWORD_REQUIRED,
 * TOTP, etc.). The challengeResponse is whatever the challenge needs:
 * for password challenge, the new password.
 */
export async function confirmSignInChallenge(challengeResponse: string) {
  return amplifyConfirmSignIn({ challengeResponse });
}

export async function signOut() {
  return amplifySignOut();
}

/** Kick off password reset. Cognito emails the user a confirmation code. */
export async function requestPasswordReset(email: string) {
  return amplifyResetPassword({ username: email });
}

/** Finalize password reset with the emailed code + new password. */
export async function confirmPasswordReset(args: {
  email: string;
  code: string;
  newPassword: string;
}) {
  return amplifyConfirmResetPassword({
    username: args.email,
    confirmationCode: args.code,
    newPassword: args.newPassword,
  });
}

export async function loadCurrentUser() {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    return {
      userId: user.userId,
      email: (idToken?.payload?.email as string | undefined) ?? user.signInDetails?.loginId ?? "",
      tenantId: (idToken?.payload?.["custom:tenantId"] as string | undefined) ?? null,
      role: (idToken?.payload?.["custom:role"] as string | undefined) ?? "admin",
    };
  } catch {
    return null;
  }
}

export type AuthedUser = NonNullable<Awaited<ReturnType<typeof loadCurrentUser>>>;

/** Translate Amplify error names to short, friendly messages. */
export function authErrorMessage(err: unknown): string {
  const e = err as { name?: string; message?: string };
  switch (e?.name) {
    case "UserAlreadyAuthenticatedException":
      return "You're already signed in. Refresh the page.";
    case "NotAuthorizedException":
      return "Email or password is incorrect.";
    case "UserNotConfirmedException":
      return "Please verify your email first.";
    case "UsernameExistsException":
      return "An account with this email already exists.";
    case "InvalidPasswordException":
      return "Password must be at least 12 chars with upper, lower, number, and symbol.";
    case "CodeMismatchException":
      return "That code doesn't match. Try again.";
    case "ExpiredCodeException":
      return "Code expired. Request a new one.";
    case "LimitExceededException":
      return "Too many attempts. Wait a minute and try again.";
    case "UserNotFoundException":
      return "No account found for that email.";
    case "InvalidParameterException":
      return e?.message ?? "Invalid input.";
    default:
      return e?.message ?? "Something went wrong. Try again.";
  }
}
